import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  useTheme,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Tooltip,
} from '@mui/material';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import RoomIcon from '@mui/icons-material/Room';
import moment from 'moment';
import { getRatesByRatePlan, getHotelRatePlans } from '../../services/api';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import { formatHotelDate, formatHotelDateTime } from '../../utils/dateUtils';
import { getReservationOutlinedFieldSx, getBookingDrawerSelectMenuProps } from '../../utils/bookingDrawerLayout';

const CUSTOM_BORDER_COLOR = '#4ce1b6';

const DEFAULT_MEAL_PLANS = [
  { value: 'EP', label: 'EP - European Plan' },
];

const BookingDetailsStep = ({
  checkInDate,
  checkOutDate,
  onCheckInDateChange,
  onCheckOutDateChange,
  nights,
  rooms,
  availableRoomTypes,
  loadingRoomTypes,
  onAddRoom,
  onRemoveRoom,
  roomDrawerOpen,
  onRoomDrawerOpen,
  onRoomDrawerClose,
  roomFormData,
  onRoomFormDataChange,
  bookingType,
  onBookingTypeChange,
}) => {
  const theme = useTheme();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  const formatCurrency = (value) => `₹${value.toFixed(2)}`;

  const roomDrawerSurface = isDark ? '#2a2a31' : '#ffffff';
  const roomDrawerFieldSx = getReservationOutlinedFieldSx(isDark, roomDrawerSurface);
  const reservationDateFieldSx = getReservationOutlinedFieldSx(isDark, theme.palette.background.paper);
  const drawerSelectMenuProps = useMemo(
    () => getBookingDrawerSelectMenuProps(isDark, roomDrawerSurface),
    [roomDrawerSurface, isDark],
  );
  const selectedRoomTypeId = roomFormData.roomTypeId ? String(roomFormData.roomTypeId) : '';

  // Calculate total hours for hourly bookings
  const calculateTotalHours = () => {
    if (bookingType === 'hourly' && checkInDate && checkOutDate) {
      const startTime = checkInDate.clone();
      const endTime = checkOutDate.clone();
      const diffInHours = endTime.diff(startTime, 'hours', true); // true for decimal precision
      return Math.max(0, diffInHours);
    }
    return 0;
  };

  const totalHours = calculateTotalHours();

  // Get selected room type for validation
  // Ensure robust comparison by converting both to strings, but only if roomTypeId is truthy
  const selectedRoomType = roomFormData.roomTypeId 
    ? availableRoomTypes.find(rt => {
        const rtIdStr = String(rt.id);
        const formIdStr = String(roomFormData.roomTypeId);
        const matches = rtIdStr === formIdStr;
        return matches;
      })
    : null;
  const maxAvailableRooms = selectedRoomType?.available_rooms ?? 0;
  
  // Get time slots for selected room type (for hourly booking)
  const timeSlots = selectedRoomType?.slots || [];
  
  // Get room type constraints
  const baseAdults = selectedRoomType?.base_adults || 2;
  const baseChildren = selectedRoomType?.base_children || 0;
  const maxGuests = selectedRoomType?.max_guests || 4;
  
  // Max totals based on number of rooms and total capacity
  // Total capacity is rooms * max_guests
  const maxTotalCapacity = roomFormData.rooms * maxGuests;
  
  // Maximum adults is total capacity minus current children
  const maxTotalAdults = maxTotalCapacity - roomFormData.children;
  
  // Maximum children is total capacity minus current adults
  const maxTotalChildren = maxTotalCapacity - roomFormData.adults;
  
  // State for rates based on meal plan
  const [mealPlanRates, setMealPlanRates] = useState(null);
  
  // State for meal plans from API (using same API as walk-in drawer)
  const [mealPlans, setMealPlans] = useState(DEFAULT_MEAL_PLANS);
  const [loadingMealPlans, setLoadingMealPlans] = useState(false);
  
  // Fetch meal plans on component mount (using same API as walk-in drawer)
  useEffect(() => {
    const fetchMealPlans = async () => {
      setLoadingMealPlans(true);
      try {
        const params = {};
        if (checkInDate) params.check_in_date = checkInDate.format('YYYY-MM-DD');
        if (checkOutDate) params.check_out_date = checkOutDate.format('YYYY-MM-DD');
        if (roomFormData.roomTypeId) params.room_type_id = roomFormData.roomTypeId;
        const data = await getHotelRatePlans({ params });
        // Handle different possible response structures
        const ratePlans = data?.rate_plans || data?.ratePlans || data || [];
        
        if (Array.isArray(ratePlans) && ratePlans.length > 0) {
          // Map rate plans to meal plan format (same as RoomsStep)
          const mealPlansData = ratePlans.map((rp) => ({
            value: rp.code,
            label: rp.name || rp.code,
            id: rp.id,
            meal_plan: rp.meal_plan,
          }));
          setMealPlans(mealPlansData);
          
          const selectedStillAvailable = mealPlansData.some(
            (plan) => plan.value === roomFormData.mealPlan
          );
          if (!roomFormData.mealPlan || !selectedStillAvailable) {
            onRoomFormDataChange({ ...roomFormData, mealPlan: mealPlansData[0].value });
          }
        } else {
          setMealPlans([]);
          if (roomFormData.mealPlan) {
            onRoomFormDataChange({ ...roomFormData, mealPlan: '' });
          }
        }
      } catch (error) {// Keep default meal plans on error
        setMealPlans(DEFAULT_MEAL_PLANS);
      } finally {
        setLoadingMealPlans(false);
      }
    };
    fetchMealPlans();
  }, [checkInDate, checkOutDate, roomFormData.roomTypeId]);
  
  // Get rates from selected room type for the check-in date
  // The API already returns rates filtered by check-in/check-out dates
  // Use base_rate as primary (source of truth), fallback to final_rate
  // Convert to numbers to ensure proper comparison, handling null/undefined
  const parseRate = (value) => {
    if (value === null || value === undefined) return 0;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };
  
  // Use meal plan rates if available, otherwise fall back to room type rates
  const ratesToUse = mealPlanRates || selectedRoomType || {};
  const baseRate = parseRate(ratesToUse.base_rate ?? ratesToUse.final_rate);
  const singlePersonRate = parseRate(ratesToUse.single_occupancy_rate ?? ratesToUse.base_rate);
  const multiPersonRate = baseRate; // Base rate is typically for base_adults
  
  // Extra rates for additional adults/children
  const extraAdultRate = parseRate(ratesToUse.extra_adult_rate);
  const extraChildRate = parseRate(ratesToUse.extra_child_rate);
  
  // Calculate extra adults and children beyond base capacity
  const baseAdultsCapacity = roomFormData.rooms * baseAdults;
  const baseChildrenCapacity = roomFormData.rooms * baseChildren;
  const extraAdultsCount = Math.max(0, roomFormData.adults - baseAdultsCapacity);
  const extraChildrenCount = Math.max(0, roomFormData.children - baseChildrenCapacity);
  
  // Auto-fill extra charges when rates are available from API
  // Reset charges to 0 when there are no extra adults/children
  // Update charges when meal plan changes (extra rates come from meal plan)
  useEffect(() => {
    if (selectedRoomType && roomFormData.roomTypeId) {
      const updates = {};
      
      // Handle extra adult charges
      if (extraAdultsCount > 0) {
        // Auto-fill when rate available: charge not set, OR meal plan changed (rates changed)
        if (extraAdultRate > 0) {
          const chargeNeedsUpdate = !roomFormData.extraAdultCharge || roomFormData.extraAdultCharge === 0
            || Math.abs(roomFormData.extraAdultCharge - extraAdultRate) > 0.01;
          if (chargeNeedsUpdate) {
            updates.extraAdultCharge = extraAdultRate;
          }
        }
      } else {
        // Reset to 0 if no extra adults
        if (roomFormData.extraAdultCharge > 0) {
          updates.extraAdultCharge = 0;
        }
      }
      
      // Handle extra child charges
      if (extraChildrenCount > 0) {
        // Auto-fill when rate available: charge not set, OR meal plan changed (rates changed)
        if (extraChildRate > 0) {
          const chargeNeedsUpdate = !roomFormData.extraChildCharge || roomFormData.extraChildCharge === 0
            || Math.abs(roomFormData.extraChildCharge - extraChildRate) > 0.01;
          if (chargeNeedsUpdate) {
            updates.extraChildCharge = extraChildRate;
          }
        }
      } else {
        // Reset to 0 if no extra children
        if (roomFormData.extraChildCharge > 0) {
          updates.extraChildCharge = 0;
        }
      }
      
      if (Object.keys(updates).length > 0) {
        onRoomFormDataChange({ ...roomFormData, ...updates });
      }
    }
  }, [extraAdultsCount, extraChildrenCount, extraAdultRate, extraChildRate, selectedRoomType, roomFormData.roomTypeId, mealPlanRates]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Auto-fill Room Rate/Night based on rooms, adults selection, and meal plan rates
  // Syncs input with Average Rate/Night card - updates when meal plan changes or adults/rooms change
  useEffect(() => {
    if (selectedRoomType && roomFormData.roomTypeId && roomFormData.rooms > 0 && roomFormData.adults > 0) {
      // Calculate adults per room
      const adultsPerRoom = roomFormData.adults / roomFormData.rooms;
      
      // Determine which rate to use (matches logic in Average Rate/Night card)
      let rateToUse = 0;
      if (adultsPerRoom === 1) {
        // Single occupancy (exactly 1 adult per room) - use single occupancy rate
        rateToUse = singlePersonRate;
      } else if (adultsPerRoom > 1) {
        // More than 1 adult per room - use base rate
        rateToUse = baseRate;
      }
      
      const currentRate = roomFormData.ratePerNight || 0;
      const needsUpdate = rateToUse > 0 && Math.abs(currentRate - rateToUse) > 0.01;
      
      // Auto-fill when: rate is 0, OR rate needs update (e.g. meal plan changed, adults changed)
      // When meal plan changes, mealPlanRates updates → baseRate/singlePersonRate change → needsUpdate is true
      const matchesSingleRate = Math.abs(currentRate - singlePersonRate) < 0.01;
      const matchesBaseRate = Math.abs(currentRate - baseRate) < 0.01;
      const shouldAutoFill = currentRate === 0
        || (needsUpdate && (matchesSingleRate || matchesBaseRate))
        || (mealPlanRates && needsUpdate);
      
      if (rateToUse > 0 && shouldAutoFill) {
        onRoomFormDataChange({ ...roomFormData, ratePerNight: rateToUse });
      }
    }
  }, [selectedRoomType, roomFormData.roomTypeId, roomFormData.rooms, roomFormData.adults, baseRate, singlePersonRate, mealPlanRates]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Fetch rates when meal plan, room type, or check-in date changes
  useEffect(() => {
    if (roomFormData.roomTypeId && roomFormData.mealPlan && checkInDate && bookingType === 'nightly') {
      getRatesByRatePlan(
        roomFormData.roomTypeId,
        checkInDate.format('YYYY-MM-DD'),
        roomFormData.mealPlan,
        checkOutDate?.format('YYYY-MM-DD')
      )
        .then((data) => {
          if (data.rates) {
            setMealPlanRates(data.rates);
          } else {
            setMealPlanRates(null);
          }
        })
        .catch((error) => {setMealPlanRates(null);
        });
    } else {
      setMealPlanRates(null);
    }
  }, [roomFormData.roomTypeId, roomFormData.mealPlan, checkInDate, checkOutDate, bookingType]);
  
  // When booking type changes to hourly, set meal plan to first available (usually EP) and sync checkout date/time
  useEffect(() => {
    if (bookingType === 'hourly') {
      // Set meal plan to first available meal plan (usually EP)
      const defaultMealPlan = mealPlans[0]?.value || 'EP';
      if (!roomFormData.mealPlan || roomFormData.mealPlan !== defaultMealPlan) {
        onRoomFormDataChange({ ...roomFormData, mealPlan: defaultMealPlan });
      }
      // Sync checkout date with check-in date and set default checkout time to check-in + 1 hour
      if (checkInDate) {
        const checkoutDateTime = checkInDate.clone();
        if (!checkOutDate || !checkOutDate.isSame(checkInDate, 'day')) {
          // Set checkout time to check-in + 1 hour by default
          checkoutDateTime.add(1, 'hour');
          onCheckOutDateChange(checkoutDateTime);
        } else {
          // Keep the same day but ensure it's properly set
          const currentTime = checkOutDate.clone();
          checkoutDateTime.hour(currentTime.hour());
          checkoutDateTime.minute(currentTime.minute());
          onCheckOutDateChange(checkoutDateTime);
        }
      }
    }
  }, [bookingType, checkInDate]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // If rates are provided as arrays or date-specific, we should use the first rate for check-in date
  // For now, using the base_rate which should already be date-filtered from the API
  // The rates displayed are for the selected check-in date from availableRoomTypes

  const handleNumberChange = (field, delta) => {
    const current = roomFormData[field] || 0;
    let newValue = Math.max(0, current + delta);
    
    // Validate rooms against available inventory
    if (field === 'rooms') {
      // Always enforce available rooms limit when a room type is selected
      if (roomFormData.roomTypeId && selectedRoomType) {
        // Check if room type is sold out or blocked
        const isSoldOut = selectedRoomType.is_sold_out || selectedRoomType.available_rooms === 0;
        const isBlocked = selectedRoomType.is_blocked;
        
        // If sold out or blocked, cannot select any rooms
        if (isSoldOut || isBlocked) {
          newValue = 0;
        } else {
        // Ensure rooms cannot exceed available rooms for the selected room type
        newValue = Math.min(newValue, maxAvailableRooms);
        // If available rooms is 0, user cannot select any rooms
        if (maxAvailableRooms === 0) {
          newValue = 0;
          }
        }
      }
      // When rooms change, validate total capacity
      if (newValue > 0) {
        const newMaxCapacity = newValue * maxGuests;
        const totalGuests = roomFormData.adults + roomFormData.children;
        if (totalGuests > newMaxCapacity) {
          // Adjust to fit within new capacity, prioritizing adults
          const newAdults = Math.min(roomFormData.adults, newMaxCapacity);
          const newChildren = newMaxCapacity - newAdults;
          onRoomFormDataChange({ 
            ...roomFormData, 
            rooms: newValue,
            adults: newAdults,
            children: newChildren,
          });
          return;
        }
      }
    } else if (field === 'adults') {
      // Validate adults - if rooms > 0, validate against capacity
      if (roomFormData.rooms > 0) {
        const maxAllowed = (roomFormData.rooms * maxGuests) - roomFormData.children;
        newValue = Math.min(newValue, maxAllowed);
      } else {
        // If rooms is 0, allow setting adults (will be validated when rooms are added)
        // Set a reasonable upper limit (e.g., maxGuests per room)
        newValue = Math.min(newValue, maxGuests);
      }
    } else if (field === 'children') {
      // Validate children - if rooms > 0, validate against capacity
      if (roomFormData.rooms > 0) {
        const maxAllowed = (roomFormData.rooms * maxGuests) - roomFormData.adults;
        newValue = Math.min(newValue, maxAllowed);
      } else {
        // If rooms is 0, allow setting children (will be validated when rooms are added)
        newValue = Math.min(newValue, maxGuests);
      }
    }
    
    onRoomFormDataChange({ ...roomFormData, [field]: newValue });
  };

  const handleInputChange = (field, value) => {
    let newValue = value;
    
    // Validate rooms against available inventory
    if (field === 'rooms') {
      const parsed = parseInt(value) || 0;
      // Always enforce available rooms limit when a room type is selected
      if (roomFormData.roomTypeId && selectedRoomType) {
        // Check if room type is sold out or blocked
        const isSoldOut = selectedRoomType.is_sold_out || selectedRoomType.available_rooms === 0;
        const isBlocked = selectedRoomType.is_blocked;
        
        // If sold out or blocked, cannot select any rooms
        if (isSoldOut || isBlocked) {
          newValue = 0;
        } else {
        // Ensure rooms cannot exceed available rooms for the selected room type
        newValue = Math.min(Math.max(0, parsed), maxAvailableRooms);
        // If available rooms is 0, user cannot select any rooms
        if (maxAvailableRooms === 0) {
          newValue = 0;
          }
        }
      } else {
        // If no room type selected, allow 0
        newValue = Math.max(0, parsed);
      }
    } else if (field === 'roomTypeId') {
      // When room type changes, validate and reset rooms if necessary
      // Ensure value is stored as string for consistent comparison
      newValue = String(value || '');
      const newSelectedRoomType = availableRoomTypes.find(rt => {
        const rtIdStr = String(rt.id);
        return rtIdStr === newValue;
      });
      const newMaxAvailableRooms = newSelectedRoomType?.available_rooms ?? 0;
      
      // If current rooms exceed available rooms for new room type, reset to available rooms
      let updatedRooms = roomFormData.rooms;
      if (updatedRooms > newMaxAvailableRooms) {
        updatedRooms = newMaxAvailableRooms;
      }
      // If available rooms is 0, reset rooms to 0
      if (newMaxAvailableRooms === 0) {
        updatedRooms = 0;
      }
      
      // Update with new room type and validated rooms
      onRoomFormDataChange({ 
        ...roomFormData, 
        roomTypeId: newValue,
        rooms: updatedRooms,
        // Reset meal plan to default when room type changes
        mealPlan: mealPlans[0]?.value || 'EP',
        // Reset extra charges when room type changes
        extraAdultCharge: 0,
        extraChildCharge: 0,
      });
      // Reset meal plan rates when room type changes
      setMealPlanRates(null);
      // Fetch rates for default meal plan
      if (newValue && checkInDate) {
        getRatesByRatePlan(
          newValue,
          checkInDate.format('YYYY-MM-DD'),
          mealPlans[0]?.value || 'EP',
          checkOutDate?.format('YYYY-MM-DD')
        )
          .then((data) => {
            if (data.rates) {
              setMealPlanRates(data.rates);
            }
          })
          .catch((error) => {});
      }
      return;
    } else if (field === 'mealPlan') {
      // String field - keep as is
      newValue = value;
      // Fetch rates for the new meal plan
      if (roomFormData.roomTypeId && checkInDate) {
        getRatesByRatePlan(
          roomFormData.roomTypeId,
          checkInDate.format('YYYY-MM-DD'),
          newValue,
          checkOutDate?.format('YYYY-MM-DD')
        )
          .then((data) => {
            if (data.rates) {
              setMealPlanRates(data.rates);
            }
          })
          .catch((error) => {setMealPlanRates(null);
          });
      }
    } else if (field === 'ratePerNight') {
      // Decimal field
      newValue = parseFloat(value) || 0;
    } else if (field === 'extraAdultCharge' || field === 'extraChildCharge') {
      // Decimal fields for extra charges
      newValue = parseFloat(value) || 0;
    } else {
      // Numeric fields (adults, children)
      newValue = Math.max(0, parseInt(value) || 0);
    }
    
    onRoomFormDataChange({ ...roomFormData, [field]: newValue });
  };

  // Validate if room can be added
  const canAddRoom = roomFormData.roomTypeId && 
                     roomFormData.rooms > 0 && 
                     (roomFormData.adults > 0 || roomFormData.children > 0) && 
                     roomFormData.ratePerNight > 0 &&
                     selectedRoomType &&
                     maxAvailableRooms > 0;

  return (
    <Box>
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            color: theme.palette.text.primary, 
            mb: 3,
            fontWeight: 500,
          }}
        >
          Choose Check-in And Check-out Date
      </Typography>

      {/* Booking Type Toggle - Moved before date selection */}
      <Grid container spacing={3} sx={{ mb: 4, alignItems: 'center' }}>
        <Grid item xs={12} md={6}>
          <ToggleButtonGroup
            value={bookingType || 'nightly'}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) {
                if (newValue === 'hourly') {
                  return;
                }
                onBookingTypeChange(newValue);
                // When switching to nightly, set checkout to check-in + 1 day (or today + 1 if no check-in)
                if (newValue === 'nightly') {
                  if (checkInDate) {
                    onCheckOutDateChange(checkInDate.clone().add(1, 'day'));
                  } else {
                    const tomorrow = moment().add(1, 'day');
                    onCheckOutDateChange(tomorrow);
                  }
                }
              }
            }}
            aria-label="booking type"
            sx={{
              '& .MuiToggleButton-root': {
                borderColor: CUSTOM_BORDER_COLOR,
                color: theme.palette.text.primary,
                '&.Mui-selected': {
                  backgroundColor: CUSTOM_BORDER_COLOR,
                  color: '#000',
                  '&:hover': {
                    backgroundColor: CUSTOM_BORDER_COLOR,
                  },
                },
              },
            }}
          >
            <ToggleButton value="nightly" aria-label="nightly booking">
              Nightly
            </ToggleButton>
            <Tooltip title="Hourly booking will be available soon." arrow placement="top">
              <span style={{ display: 'inline-flex' }}>
                <ToggleButton value="hourly" disabled aria-label="hourly booking (coming soon)">
                  Hourly
                </ToggleButton>
              </span>
            </Tooltip>
          </ToggleButtonGroup>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
            {bookingType === 'hourly' ? (
              <AccessTimeIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />
            ) : (
              <NightsStayIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />
            )}
            <Typography variant="h5" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
              {bookingType === 'hourly' ? totalHours.toFixed(1) : nights}
              </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {bookingType === 'hourly' ? 'hour(s)' : (nights === 1 ? 'night' : 'nights')}
              </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Date and Time Selection */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {bookingType === 'hourly' ? (
          <>
            {/* For hourly: Check-in Date (full width) + Start time + End time (side by side) */}
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Check-in"
                value={checkInDate}
                onChange={(newValue) => {
                  if (newValue && newValue.isValid()) {
                    // Update both check-in and check-out dates to the same date
                    const newCheckIn = newValue.clone();
                    const newCheckOut = newValue.clone();
                    // Preserve existing times
                    if (checkInDate) {
                      newCheckIn.hour(checkInDate.hour());
                      newCheckIn.minute(checkInDate.minute());
                    }
                    if (checkOutDate) {
                      newCheckOut.hour(checkOutDate.hour());
                      newCheckOut.minute(checkOutDate.minute());
                    } else {
                      // Default to +1 hour if no checkout time set
                      newCheckOut.hour(newCheckIn.hour() + 1);
                    }
                    onCheckInDateChange(newCheckIn);
                    onCheckOutDateChange(newCheckOut);
                  }
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputLabelProps: { shrink: true },
                    sx: {
                      ...reservationDateFieldSx,
                      '& .MuiOutlinedInput-root input': {
                        color: theme.palette.text.primary,
                      },
                    },
                  },
                  paperContent: {
                    sx: {
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TimePicker
                label="Start Time"
                value={checkInDate}
                onChange={(newValue) => {
                  if (newValue && newValue.isValid() && checkInDate) {
                    // Update check-in time, keeping the date
                    const updatedDateTime = checkInDate.clone();
                    updatedDateTime.hour(newValue.hour());
                    updatedDateTime.minute(newValue.minute());
                    updatedDateTime.second(0);
                    updatedDateTime.millisecond(0);
                    onCheckInDateChange(updatedDateTime);
                    // If checkout time is before new check-in time, adjust it
                    if (checkOutDate && checkOutDate.isBefore(updatedDateTime)) {
                      const newCheckOut = updatedDateTime.clone().add(1, 'hour');
                      onCheckOutDateChange(newCheckOut);
                    }
                  }
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputLabelProps: { shrink: true },
                    sx: {
                      ...reservationDateFieldSx,
                      '& .MuiOutlinedInput-root': {
                        ...reservationDateFieldSx['& .MuiOutlinedInput-root'],
                        '& fieldset': {
                          borderColor: CUSTOM_BORDER_COLOR,
                          borderWidth: 2,
                        },
                        '&:hover fieldset': {
                          borderColor: CUSTOM_BORDER_COLOR,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: CUSTOM_BORDER_COLOR,
                        },
                        '& input': {
                          color: theme.palette.text.primary,
                          fontSize: '1.1rem',
                          padding: '16px 14px',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: theme.palette.text.secondary,
                        fontSize: '1rem',
                      },
                    },
                  },
                  openPickerIcon: {
                    sx: {
                      color: theme.palette.text.secondary,
                      fontSize: '1.5rem',
                    },
                  },
                  popper: {
                    sx: {
                      '& .MuiPaper-root': {
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                      },
                    },
                  },
                  digitalClockSectionItem: {
                    sx: {
                      fontSize: '1.2rem',
                      minHeight: 48,
                      '&.Mui-selected': {
                        backgroundColor: CUSTOM_BORDER_COLOR,
                        color: '#000',
                        fontWeight: 600,
                        fontSize: '1.3rem',
                        '&:hover': {
                          backgroundColor: CUSTOM_BORDER_COLOR,
                        },
                      },
                    },
                  },
                }}
                views={['hours', 'minutes']}
                timeSteps={{ hours: 1, minutes: 5 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TimePicker
                label="End Time"
                value={checkOutDate}
                onChange={(newValue) => {
                  if (newValue && newValue.isValid() && checkInDate) {
                    // Keep the same date as check-in, only update the time
                    const updatedDateTime = checkInDate.clone();
                    updatedDateTime.hour(newValue.hour());
                    updatedDateTime.minute(newValue.minute());
                    updatedDateTime.second(0);
                    updatedDateTime.millisecond(0);
                    // Ensure end time is after start time
                    if (updatedDateTime.isAfter(checkInDate)) {
                      onCheckOutDateChange(updatedDateTime);
                    }
                  }
                }}
                minTime={checkInDate ? checkInDate.clone().add(1, 'minute') : undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputLabelProps: { shrink: true },
                    sx: {
                      ...reservationDateFieldSx,
                      '& .MuiOutlinedInput-root': {
                        ...reservationDateFieldSx['& .MuiOutlinedInput-root'],
                        '& fieldset': {
                          borderColor: CUSTOM_BORDER_COLOR,
                          borderWidth: 2,
                        },
                        '&:hover fieldset': {
                          borderColor: CUSTOM_BORDER_COLOR,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: CUSTOM_BORDER_COLOR,
                        },
                        '& input': {
                          color: theme.palette.text.primary,
                          fontSize: '1.1rem',
                          padding: '16px 14px',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: theme.palette.text.secondary,
                        fontSize: '1rem',
                      },
                    },
                  },
                  openPickerIcon: {
                    sx: {
                      color: theme.palette.text.secondary,
                      fontSize: '1.5rem',
                    },
                  },
                  popper: {
                    sx: {
                      '& .MuiPaper-root': {
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                      },
                    },
                  },
                  digitalClockSectionItem: {
                    sx: {
                      fontSize: '1.2rem',
                      minHeight: 48,
                      '&.Mui-selected': {
                        backgroundColor: CUSTOM_BORDER_COLOR,
                        color: '#000',
                        fontWeight: 600,
                        fontSize: '1.3rem',
                        '&:hover': {
                          backgroundColor: CUSTOM_BORDER_COLOR,
                        },
                      },
                    },
                  },
                }}
                views={['hours', 'minutes']}
                timeSteps={{ hours: 1, minutes: 5 }}
              />
            </Grid>
          </>
        ) : (
          <>
            {/* For nightly: Check-in date + Check-out date */}
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Check-in"
                value={checkInDate}
                onChange={(newValue) => {
                  if (newValue && newValue.isValid()) {
                    onCheckInDateChange(newValue);
                  }
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputLabelProps: { shrink: true },
                    sx: {
                      ...reservationDateFieldSx,
                      '& .MuiOutlinedInput-root input': {
                        color: theme.palette.text.primary,
                      },
                    },
                  },
                  paperContent: {
                    sx: {
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Check-out"
                value={checkOutDate}
                onChange={(newValue) => {
                  if (newValue && newValue.isValid()) {
                    onCheckOutDateChange(newValue);
                  }
                }}
                minDate={checkInDate ? checkInDate.clone().add(1, 'day') : moment().add(1, 'day')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  InputLabelProps: { shrink: true },
                  sx: {
                    ...reservationDateFieldSx,
                    '& .MuiOutlinedInput-root input': {
                      color: theme.palette.text.primary,
                    },
                  },
                },
                paperContent: {
                  sx: {
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                  },
                },
            }}
          />
        </Grid>
          </>
        )}
      </Grid>

      {/* Room Details Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>Room Details</Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onRoomDrawerOpen}
          >
            Add Room Type
          </Button>
        </Box>

        {rooms.length === 0 ? (
          <div style={{ 
            padding: '30px', 
            textAlign: 'center',
            borderRadius: '5px',
            backgroundColor: 'transparent',
          }}>
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
              No rooms added yet. Click "Add Room Type" to get started.
            </Typography>
          </div>
        ) : (
          <Grid container spacing={2}>
            {rooms.map((room, index) => (
              <Grid item xs={12} key={index}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                          {room.roomType}
                          {room.mealPlan !== 'EP' && (
                            <Chip label={room.mealPlan} size="small" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
                          Adults: {room.adults} | Children: {room.children} | Rooms: {room.rooms}
                        </Typography>
                        {(room.checkInDate && room.checkOutDate) ? (
                          <Typography variant="body2" sx={{ mt: 0.5, color: theme.palette.text.secondary, fontSize: '0.8rem' }}>
                            {bookingType === 'hourly'
                              ? `${moment(room.checkInDate).format('DD MMM YYYY, hh:mm A')} – ${moment(room.checkOutDate).format('DD MMM YYYY, hh:mm A')}`
                              : `${moment(room.checkInDate).format('DD MMM YYYY')} – ${moment(room.checkOutDate).format('DD MMM YYYY')}`}
                          </Typography>
                        ) : (checkInDate && checkOutDate) && (
                          <Typography variant="body2" sx={{ mt: 0.5, color: theme.palette.text.secondary, fontSize: '0.8rem' }}>
                            {bookingType === 'hourly'
                              ? `${checkInDate.format('DD MMM YYYY, hh:mm A')} – ${checkOutDate.format('DD MMM YYYY, hh:mm A')}`
                              : `${checkInDate.format('DD MMM YYYY')} – ${checkOutDate.format('DD MMM YYYY')}`}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <IconButton
                          color="error"
                          onClick={() => onRemoveRoom(index)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                        <Typography variant="h6" color="primary">
                          {formatCurrency(room.total)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>


      {/* Room Type Drawer */}
      <Drawer
        anchor="right"
        open={roomDrawerOpen}
        onClose={onRoomDrawerClose}
        PaperProps={{ 
          sx: { 
            width: { xs: '100%', sm: 600 },
            backgroundColor: roomDrawerSurface,
            overflow: 'visible',
          } 
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              borderBottom: `2px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              backgroundColor: isDark ? '#2a2a31' : '#ffffff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RoomIcon sx={{ color: CUSTOM_BORDER_COLOR, fontSize: 24 }} />
              <Typography variant="h6" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600 }}>
                Select Room Type
              </Typography>
            </Box>
            <IconButton 
              onClick={onRoomDrawerClose} 
              size="small"
              sx={{ color: isDark ? '#999' : '#666', p: 0.5 }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          {/* Content */}
          <Box
            sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', p: 2, ...roomDrawerFieldSx }}
          >

            {/* Room Type Selection */}
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                mb: 1.5,
                backgroundColor: roomDrawerSurface,
                border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                borderRadius: '12px',
                overflow: 'visible',
              }}
            >
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="choose-room-type-label" shrink>
                  Choose Room Type
                </InputLabel>
                <Select
                  labelId="choose-room-type-label"
                  value={selectedRoomTypeId}
                  label="Choose Room Type"
                  displayEmpty
                  onChange={(e) => handleInputChange('roomTypeId', e.target.value)}
                  disabled={loadingRoomTypes}
                  MenuProps={drawerSelectMenuProps}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ color: isDark ? '#999' : '#666', fontStyle: 'italic' }}
                        >
                          Choose Room Type
                        </Typography>
                      );
                    }
                    const roomType = availableRoomTypes.find((rt) => String(rt.id) === String(selected));
                    return roomType
                      ? `${roomType.name} (${roomType.available_rooms} available)`
                      : String(selected);
                  }}
                  sx={{
                    color: isDark ? '#ddd' : '#333',
                    '& .MuiSelect-select': {
                      color: isDark ? '#ddd' : '#333',
                    },
                  }}
                >
              <MenuItem value="">
                <em>Choose Room Type</em>
              </MenuItem>
              {availableRoomTypes.map((roomType) => {
                // Determine status: sold out takes priority, then blocked
                // Sold out: explicitly marked as sold_out OR available_rooms is 0 (fully booked) OR is_fully_booked flag
                // Blocked: inventory explicitly blocked (is_blocked = true)
                const isSoldOut = roomType.is_sold_out || roomType.is_fully_booked || (roomType.available_rooms === 0 && !roomType.is_blocked);
                const isBlocked = Boolean(roomType.is_blocked);  // Convert to boolean explicitly
                const statusText = isSoldOut 
                  ? 'Sold Out' 
                  : isBlocked 
                    ? 'Blocked' 
                    : '';
                // Disable if sold out OR blocked - blocked should always prevent selection
                // Blocked items must be disabled to prevent selection
                const isDisabled = isSoldOut || isBlocked;
                
                return (
                <MenuItem
                  key={roomType.id}
                  value={String(roomType.id)}
                    disabled={isDisabled}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                      <Typography variant="body2">
                  {roomType.name} ({roomType.available_rooms} available)
                      </Typography>
                      {statusText && (
                        <Chip 
                          label={statusText} 
                          size="small" 
                          color={isSoldOut ? 'error' : 'warning'}
                          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="choose-meal-plan-label" shrink>
              Choose Meal Plan
            </InputLabel>
            <Select
                labelId="choose-meal-plan-label"
                value={roomFormData.mealPlan || mealPlans[0]?.value || 'EP'}
              label="Choose Meal Plan"
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  handleInputChange('mealPlan', selectedValue);
                }}
                disabled={!selectedRoomTypeId || bookingType === 'hourly'}
                MenuProps={drawerSelectMenuProps}
                sx={{
                  color: isDark ? '#ddd' : '#333',
                  '& .MuiSelect-select': {
                    color: isDark ? '#ddd' : '#333',
                  },
                }}
                renderValue={(selected) => {
                  const selectedPlan = mealPlans.find((plan) => plan.value === selected);
                  return selectedPlan ? selectedPlan.label : (mealPlans[0]?.label || mealPlans[0]?.value || 'EP');
                }}
            >
              {mealPlans.map((plan) => (
                <MenuItem key={plan.value} value={plan.value}>
                  {plan.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
            </Paper>

            {/* Time Slots Display - Show when hourly booking and room type selected */}
            {bookingType === 'hourly' && selectedRoomType && timeSlots.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 1.5,
                  backgroundColor: roomDrawerSurface,
                  border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                  borderRadius: '12px',
                  overflow: 'visible',
                }}
              >
                <Typography variant="subtitle2" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600, mb: 1.5 }}>
                  Available Time Slots
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 300, overflowY: 'auto' }}>
                  {timeSlots.map((slot, index) => (
                    <Box 
                      key={slot.id || index}
                      sx={{ 
                        p: 1.5, 
                        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)'}`, 
                        borderRadius: 1,
                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.05)' : 'rgba(76, 225, 182, 0.03)',
                      }}
                    >
                      <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 500 }}>
                        {formatHotelDateTime(slot.start_ts)} - {moment(slot.end_ts).format('HH:mm')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.75rem' }}>
                        Available: {slot.available_rooms || 0} room(s)
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}

            {/* Rate Display - Show when room type and check-in date are selected (for nightly booking) */}
            {bookingType === 'nightly' && selectedRoomType && checkInDate && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 1.5,
                  backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                  border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                  borderRadius: '12px',
                }}
              >
                <Typography variant="subtitle2" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600, mb: 1 }}>
                  Average Rate/Night
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', mb: 1.5, display: 'block', fontSize: '0.7rem' }}>
                  For {formatHotelDate(checkInDate)}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label="1"
                      size="small"
                      sx={{
                        backgroundColor: CUSTOM_BORDER_COLOR,
                        color: '#000',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        height: 24,
                        width: 32,
                      }}
                    />
                    <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', fontSize: '0.85rem' }}>
                      x 1 (Single)
                    </Typography>
                    <Typography variant="body2" sx={{ color: isDark ? '#fff' : '#212121', fontWeight: 700, ml: 'auto', fontSize: '0.85rem' }}>
                      {singlePersonRate > 0 ? formatCurrency(singlePersonRate) : 'N/A'}
                    </Typography>
                  </Box>
                  
                  {/* Base Rate (base_adults persons) */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={baseAdults}
                      size="small"
                      sx={{
                        backgroundColor: CUSTOM_BORDER_COLOR,
                        color: '#000',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        height: 24,
                        width: 32,
                      }}
                    />
                    <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', fontSize: '0.85rem' }}>
                      x {baseAdults} (Base)
                    </Typography>
                    <Typography variant="body2" sx={{ color: isDark ? '#fff' : '#212121', fontWeight: 700, ml: 'auto', fontSize: '0.85rem' }}>
                      {multiPersonRate > 0 ? formatCurrency(multiPersonRate) : 'N/A'}
                    </Typography>
                  </Box>
                  
                  {/* Extra Adult Rate (if available) */}
                  {extraAdultRate > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 4 }}>
                      <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.8rem' }}>
                        Extra Adult
                      </Typography>
                      <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', ml: 'auto', fontSize: '0.8rem', fontWeight: 500 }}>
                        {formatCurrency(extraAdultRate)} /person
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Extra Child Rate (if available) */}
                  {extraChildRate > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 4 }}>
                      <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.8rem' }}>
                        Extra Child
                      </Typography>
                      <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333', ml: 'auto', fontSize: '0.8rem', fontWeight: 500 }}>
                        {formatCurrency(extraChildRate)} /person
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}

            {/* Room Configuration */}
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                mb: 1.5,
                backgroundColor: roomDrawerSurface,
                border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                borderRadius: '12px',
                overflow: 'visible',
              }}
            >
              <Typography variant="subtitle2" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600, mb: 1.5 }}>
                Room Configuration
              </Typography>

              {/* Rooms Selection */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.85rem' }}>
                    Rooms {selectedRoomType && `(Available: ${maxAvailableRooms})`}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleNumberChange('rooms', -1)}
                      disabled={roomFormData.rooms === 0 || !roomFormData.roomTypeId}
                      sx={{
                        border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                        color: CUSTOM_BORDER_COLOR,
                        '&:hover': {
                          border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                        },
                        '&.Mui-disabled': {
                          border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                          color: isDark ? '#666' : '#999',
                        },
                      }}
                    >
                      <RemoveIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        minWidth: 60,
                        textAlign: 'center',
                        color: isDark ? '#fff' : '#212121',
                        fontWeight: 700,
                        fontSize: '1rem',
                      }}
                    >
                      {roomFormData.rooms}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleNumberChange('rooms', 1)}
                      disabled={
                        !roomFormData.roomTypeId || 
                        !selectedRoomType || 
                        maxAvailableRooms === 0 || 
                        roomFormData.rooms >= maxAvailableRooms ||
                        selectedRoomType.is_sold_out ||
                        selectedRoomType.is_blocked
                      }
                      sx={{
                        border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                        color: CUSTOM_BORDER_COLOR,
                        '&:hover': {
                          border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                        },
                        '&.Mui-disabled': {
                          border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                          color: isDark ? '#666' : '#999',
                        },
                      }}
                    >
                      <AddIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>

              {/* Adults Selection */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.85rem' }}>
                    Adults {roomFormData.rooms > 0 && `(Max: ${maxTotalCapacity - roomFormData.children} for ${roomFormData.rooms} room${roomFormData.rooms > 1 ? 's' : ''})`}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleNumberChange('adults', -1)}
                      disabled={!roomFormData.roomTypeId || roomFormData.adults === 0}
                      sx={{
                        border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                        color: CUSTOM_BORDER_COLOR,
                        '&:hover': {
                          border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                        },
                        '&.Mui-disabled': {
                          border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                          color: isDark ? '#666' : '#999',
                        },
                      }}
                    >
                      <RemoveIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        minWidth: 60,
                        textAlign: 'center',
                        color: isDark ? '#fff' : '#212121',
                        fontWeight: 700,
                        fontSize: '1rem',
                      }}
                    >
                      {roomFormData.adults}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleNumberChange('adults', 1)}
                      disabled={!roomFormData.roomTypeId || (roomFormData.rooms > 0 && roomFormData.adults >= (maxTotalCapacity - roomFormData.children))}
                      sx={{
                        border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                        color: CUSTOM_BORDER_COLOR,
                        '&:hover': {
                          border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                        },
                        '&.Mui-disabled': {
                          border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                          color: isDark ? '#666' : '#999',
                        },
                      }}
                    >
                      <AddIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
                {(roomFormData.adults + roomFormData.children) > maxTotalCapacity && (
                  <Typography variant="caption" sx={{ color: isDark ? '#f44336' : '#d32f2f', mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                    Total guests ({roomFormData.adults + roomFormData.children}) exceeds capacity ({maxTotalCapacity}) for {roomFormData.rooms} room{roomFormData.rooms > 1 ? 's' : ''}
                  </Typography>
                )}
              </Box>

              {/* Extra Adult Charge Input - Show when adults exceed base capacity */}
              {extraAdultsCount > 0 && (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Extra Adult Charge/Night"
                    type="number"
                    value={roomFormData.extraAdultCharge || 0}
                    onChange={(e) => handleInputChange('extraAdultCharge', parseFloat(e.target.value) || 0)}
                    inputProps={{ step: 0.01, min: 0 }}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              )}

              {/* Children Selection */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.85rem' }}>
                    Children {roomFormData.rooms > 0 && `(Max: ${maxTotalCapacity - roomFormData.adults} for ${roomFormData.rooms} room${roomFormData.rooms > 1 ? 's' : ''})`}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleNumberChange('children', -1)}
                      disabled={!roomFormData.roomTypeId || roomFormData.children === 0}
                      sx={{
                        border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                        color: CUSTOM_BORDER_COLOR,
                        '&:hover': {
                          border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                        },
                        '&.Mui-disabled': {
                          border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                          color: isDark ? '#666' : '#999',
                        },
                      }}
                    >
                      <RemoveIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        minWidth: 60,
                        textAlign: 'center',
                        color: isDark ? '#fff' : '#212121',
                        fontWeight: 700,
                        fontSize: '1rem',
                      }}
                    >
                      {roomFormData.children}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleNumberChange('children', 1)}
                      disabled={!roomFormData.roomTypeId || (roomFormData.rooms > 0 && roomFormData.children >= (maxTotalCapacity - roomFormData.adults))}
                      sx={{
                        border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                        color: CUSTOM_BORDER_COLOR,
                        '&:hover': {
                          border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                        },
                        '&.Mui-disabled': {
                          border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                          color: isDark ? '#666' : '#999',
                        },
                      }}
                    >
                      <AddIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>

              {/* Extra Child Charge Input - Show when children exceed base capacity */}
              {extraChildrenCount > 0 && (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Extra Child Charge/Night"
                    type="number"
                    value={roomFormData.extraChildCharge || 0}
                    onChange={(e) => handleInputChange('extraChildCharge', parseFloat(e.target.value) || 0)}
                    inputProps={{ step: 0.01, min: 0 }}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              )}

              <TextField
                fullWidth
                label="Room Rate/Night"
                type="number"
                value={roomFormData.ratePerNight}
                onChange={(e) => handleInputChange('ratePerNight', parseFloat(e.target.value) || 0)}
                inputProps={{ step: 0.01, min: 0 }}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Paper>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 1.5,
              borderTop: `2px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              backgroundColor: isDark ? '#2a2a31' : '#ffffff',
            }}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={onRoomDrawerClose}
                  sx={{
                    borderColor: isDark ? '#333246' : '#e0e0e0',
                    color: isDark ? '#ddd' : '#666',
                    '&:hover': {
                      borderColor: CUSTOM_BORDER_COLOR,
                      backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                      color: CUSTOM_BORDER_COLOR,
                    },
                    '&:disabled': {
                      borderColor: isDark ? '#333246' : '#e0e0e0',
                      color: isDark ? '#666' : '#999',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={onAddRoom}
                  disabled={!canAddRoom}
                  sx={{
                    backgroundColor: CUSTOM_BORDER_COLOR,
                    color: '#000',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: CUSTOM_BORDER_COLOR,
                      opacity: 0.9,
                      boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                    },
                    '&:disabled': {
                      backgroundColor: isDark ? '#333246' : '#e0e0e0',
                      color: isDark ? '#999' : '#666',
                    },
                  }}
                >
                  Add Room
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default BookingDetailsStep;

